import React, { useMemo, useState, useRef } from "react";
import { View,
  Text,
  TextInput,
  StyleSheet,
  TouchableOpacity,
  Image
} from "react-native";
import { useTranslation } from 'react-i18next';
import GradientBackground from '../../../../uiComponents/GradientBackground';
import { useSessionStore } from "../../../../../../../state/src/useSessionStore";
import type { SessionState } from "../../../../../../../state/src/useSessionStore";
import { FeeLookupHelper } from "../../../../../helper/feeLookupHelper";
import EnglishFlag from "../../../../../../../../assets/images/english-flag.png";
import DownArrow from "../../../../../../../../assets/images/DownArrow.png";

type Props = {
  // onCountrySelected?: () => void;
  onEditStep?: (key: string) => void;
};

export default function BillPayReviewAndEditStep({ onEditStep }: Props) {
  const { t } = useTranslation();
  const [pin, setPin] = useState(["", "", "", ""]);
  const inputs = useRef<(TextInput | null)[]>([]);
  const pendingSendOption = useSessionStore((s: SessionState) => (s as any).pendingSendOption);
  const selectedSendOption = useSessionStore((s: SessionState) => (s as any).selectedSendOption);
  const selectedCountryCode = useSessionStore((s: SessionState) => s.selectedDestinationCountryCode);
  const selectedCountryName = useSessionStore((s: SessionState) => s.selectedDestinationCountryName);
  const selectedStateName = useSessionStore((s: SessionState) => s.selectedDestinationStateName);

  const option: any = pendingSendOption || selectedSendOption || null;

  const summary = useMemo(() => {
    if (!option) {
      return {
        sendAmount: 0,
        sendCurrency: 'USD',
        receiveAmount: 0,
        receiveCurrency: '—',
        exchangeRate: 0,
        fees: 0,
        total: 0,
      };
    }
    const sendCurrency: string = option?.SendAmounts?.SendCurrency || 'USD';
    const sendAmount: number = Number(option?.SendAmounts?.SendAmount || 0);
    const receiveCurrency: string = option?.ReceiveAmounts?.ValidCurrencyIndicator
      ? (option?.ValidReceiveCurrency || '')
      : (option?.EstimatedReceiveCurrency || option?.ValidReceiveCurrency || '');
    const receiveAmount: number = Number(option?.ValidReceiveAmount ?? option?.EstimatedReceiveAmount ?? 0);
    const exchangeRateNum: number = Number(FeeLookupHelper.getExchangeRate(option) || 0);
    const fees: number = Number(option?.SendAmounts?.TotalDiscountAmount || 0) + Number(option?.SendAmounts?.TotalSendFees || 0);
    const totalBase: number = Number(option?.TotalAmount || 0);
    const total: number = totalBase;
    return { sendAmount, sendCurrency, receiveAmount, receiveCurrency, exchangeRate: exchangeRateNum, fees, total };
  }, [pendingSendOption, selectedSendOption]);

  console.log('pendingSendOption........', pendingSendOption);

  

  const handleChange = (text: string, index: number) => {
    if (/^\d*$/.test(text)) {
      const newPin = [...pin];
      newPin[index] = text;
      setPin(newPin);

      // Move to next box automatically
      if (text && index < 3) {
        inputs.current[index + 1]?.focus();
      }

      // Backspace handling
      if (!text && index > 0) {
        inputs.current[index - 1]?.focus();
      }
    }
  };

  return (
    <View style={styles.container}>
      <Text style={styles.title}>{t("REVIEW_TITLE")}</Text>
      <Text style={styles.subtitle}>{t("REVIEW_SUBTITLE")}</Text>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: 32}}>
        <View>
          <Text style={styles.inputLabel}>{t("REVIEW_YOU_SEND")}</Text>
          <View style={styles.amountInput}>
            <Text style={styles.amountText}>{summary.sendAmount.toFixed(2)}</Text>
            <Text style={styles.currencyText}>{summary.sendCurrency}</Text>
          </View>
          <Text style={[styles.inputLabel, {marginTop: 8}]}>{t("REVIEW_THEY_RECEIVE")}</Text>
          <View style={styles.amountInput}>
            <Text style={styles.amountText}>{summary.receiveAmount.toFixed(2)}</Text>
            <Text style={styles.currencyText}>{summary.receiveCurrency || '—'}</Text>
          </View>
        </View>
        <View>
          <TouchableOpacity
            style={styles.editButton}
            onPress={() => onEditStep?.('sendingAmountEntry')}>
            <Text style={styles.editText}>Edit</Text>
          </TouchableOpacity>
        </View>
      </View>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: 38}}>
        <View>
          <Text style={[styles.inputLabel, {marginTop: 8}]}>{t("REVIEW_DESTINATION_COUNTRY")}</Text>
          <View style={[styles.amountInput, { marginTop: 2, alignItems: 'center'}]}>
            <Image
              source={EnglishFlag}
              style={{height: 20, width: 20, borderRadius: 50}}
              resizeMode='contain'           
            />
            <Text style={styles.CountryText}>{selectedCountryName}</Text>
          </View>
        </View>
        <View>
          <TouchableOpacity
            style={[styles.editButton, { backgroundColor: '#FFFFFF',}]}
            onPress={() => onEditStep?.('country')}>
            <Image
              source={DownArrow}
              style={{height: 20, width: 20, borderRadius: 50}}
              resizeMode='contain'           
            />
          </TouchableOpacity>
        </View>
      </View>
      { (selectedCountryCode === 'USA' || selectedCountryCode === 'CAN') &&
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: 38}}>
        <View>
          <Text style={[styles.inputLabel, {marginTop: 8}]}>Destination State</Text>
          <View style={[styles.amountInput, { marginTop: 2, alignItems: 'center'}]}>
            <Image
              source={EnglishFlag}
              style={{height: 20, width: 20, borderRadius: 50}}
              resizeMode='contain'           
            />
            <Text style={styles.CountryText}>{selectedStateName}</Text>
          </View>
        </View>
        <View>
          <TouchableOpacity
            style={[styles.editButton, { backgroundColor: '#FFFFFF',}]}
            onPress={() => onEditStep?.('state')}>
            <Image
              source={DownArrow}
              style={{height: 20, width: 20, borderRadius: 50}}
              resizeMode='contain'           
            />
          </TouchableOpacity>
        </View>
      </View>
      }
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: 38}}>
        <View>
          <Text style={[styles.inputLabel, {marginTop: 8}]}>How they receive</Text>
          <Text style={[styles.pickUpText]}>{pendingSendOption?.DeliveryOptDisplayName}</Text>
        </View>
        <View>
          <TouchableOpacity
            style={styles.editButton}
            onPress={() => onEditStep?.('howTheyReceive')}>
            <Text style={styles.editText}>Edit</Text>
          </TouchableOpacity>
        </View>
      </View>
      {/* Terms */}
      <View style={{ marginTop: 24 }}>
        <Text style={styles.terms}>
          I agree to MoneyGram’s <Text style={styles.link}>Terms and Conditions</Text> and confirm I am the sole beneficial owner of the assets, and am acting solely on my own profile. By continuing with this transaction I consent to the collection, use, disclosure, and transfer (including cross-border transfer) of my personal information as described in our Privacy Policy.
        </Text>
        <Text style={[styles.terms, { marginTop: 16, marginBottom: 24}]}>
          <Text style={styles.bold}>Exchange rate</Text>{"\n"}
          In addition to transfer fees, a currency exchange rate, also determined by MoneyGram, may also apply. MoneyGram may also make money from currency exchange. When selecting a money transfer operator, carefully compare both transfer fees and currency exchange rates. Transfer fees may vary depending on the amount being transferred and the delivery method. Transfer fee and currency exchange rate may be subject to change by MoneyGram without notice.
        </Text>
      </View>
      {/* Fees and CTA */}
      <GradientBackground style={styles.card}>
        <View style={styles.row}>
          <Text style={styles.feeLabel}>{t("REVIEW_EXCHANGE_RATE")}</Text>
          <View style={{ flexDirection: 'row'}}>
            <Text style={styles.oldPrice}>1 {summary.sendCurrency} = {summary.exchangeRate.toFixed(4)} {summary.receiveCurrency}</Text>
          </View>
        </View>
        <View style={styles.row}>
          <Text style={styles.feeLabel}>{t("REVIEW_FEES")}</Text>
          <View style={{ flexDirection: 'row'}}>
            <Text style={styles.oldPrice}>{summary.fees.toFixed(2)} {summary.sendCurrency}</Text>
          </View>
        </View>
        <View style={styles.row}>
          <Text style={styles.feeLabel}>{t("REVIEW_TOTAL")}</Text>
          <View style={{ flexDirection: 'row'}}>
            <Text style={styles.oldPrice}>{summary.total.toFixed(2)} {summary.sendCurrency}</Text>
          </View>
        </View>
      </GradientBackground>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: 24,
    justifyContent: "flex-start",
    marginTop: 16,
  },
  title: {
    fontSize: 22,
    fontWeight: "700",
    marginBottom: 6,
    color: "#111827",
  },
  inputGroup: {
    marginBottom: 20,
  },
  inputLabel: {
    fontSize: 12,
    fontWeight: '400',
    fontFamily: 'Rubik',
    color: '#737373',
    lineHeight: 16,
    alignSelf: 'stretch'
    // marginBottom: 8,
  },
    exchangeRate: {
    fontSize: 16,
    color: '#4CAF50',
    fontWeight: '500',
  },
  amountInput: {
    flexDirection: 'row',
    alignItems: 'baseline',
  },
  pickUpText: {
    fontFamily: 'Rubik',
    fontSize: 16,
    fontWeight: '400',
    color: '#000',
    lineHeight: 20
  },
  amountText: {
    fontFamily: 'Rubik',
    fontSize: 32,
    fontWeight: '500',
    color: '#1f1f1f',
    lineHeight: 38
  },
  amountTextGreen: {
    color: '#4CAF50',
  },
  currencyText: {
    fontFamily: 'Rubik',
    fontSize: 14,
    fontWeight: '500',
    color: '#8C8C8C',
    lineHeight: 18,
    paddingLeft: 6
  },
  editButton: {
    backgroundColor: '#E6E6E6',
    borderRadius: 6,
    paddingVertical: 4,
    paddingHorizontal: 16
  },
  editText: {
    fontFamily: 'Rubik',
    fontSize: 14,
    fontWeight: '400',
    color: '#1F1F1F',
    lineHeight: 18
  },
  CountryText: {
    fontFamily: 'Rubik',
    fontSize: 16,
    fontWeight: '400',
    color: '#1F1F1F',
    lineHeight: 24,
    paddingLeft: 6
  },
  readOnlyInput: {
    backgroundColor: '#fafafa',
  },
  subtitle: {
    fontSize: 14,
    color: "#6B7280",
  },
  pinContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 20,
  },
  pinInput: {
    width: 60,
    height: 60,
    borderWidth: 1,
    borderColor: "#D1D5DB",
    borderRadius: 12,
    textAlign: "center",
    fontSize: 22,
    fontWeight: "600",
    backgroundColor: "#F9FAFB",
    marginRight: 24
  },
  error: {
    color: "red",
    fontSize: 12,
    marginTop: 8,
  },
  card: {
    alignItems: "center",
    // width: 327,
  },
  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
    marginBottom: 16,
  },
  feeLabel: {
    fontSize: 16,
    fontWeight: "500",
  },
  oldPrice: {
    fontSize: 16,
    // textDecorationLine: "line-through",
    marginRight: 8,
  },
  newPrice: {
    fontSize: 16,
    fontWeight: "bold",
    color: "green",
  },
  button: {
    backgroundColor: "black",
    borderRadius: 12,
    paddingVertical: 12,
    paddingHorizontal: 24,
    // marginHorizontal: 30,
    width: '100%',
    alignItems: 'center',
    marginBottom: 12,
  },
  buttonText: {
    color: "white",
    fontWeight: "bold",
    fontSize: 16,
  },
  terms: {
    fontFamily: 'Rubik',
    fontSize: 12,
    fontWeight: '500',
    color: '#737373',
    lineHeight: 16,
  },
  link: { color: "#326AC2", textDecorationLine: "underline" },
  bold: { fontWeight: "600" },
});
